import React, { Fragment, useEffect, useRef, useState } from 'react';
import classnames from "classnames";
import axios from "axios";
import { Navigation, Scrollbar, Mousewheel } from 'swiper/modules';
// import SwiperCore, { Navigation, Scrollbar, Mousewheel, Lazy } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import CarouselPlaceholder from '../components/CarouselPlaceholder';

import Image from './Image';
import lazyImages from "../lazyImages/lazyImages";
import loadImageSrc from "../lazyImages/loadImageSrc";
import loadStylesheet from "../loadStylesheet";

let Carousel = props => {
    const [gallery, setGallery] = useState(null);

    const swiperContainer = useRef(null);
    const prevTags = useRef(null);
    const loadedThumbnail = useRef(null);

    let intersectObservers = [];
    let smallScreen = window.innerWidth <= 767 ? true : false;
    let isMounted;
    
    // Check if this is an event location from props or mediaTags
    const isEventLocation = props.isEventLocation || props.mediaTags === 'event-locations';
    const locationHref = isEventLocation ? `/events/${props.locationSlug}` : `/${props.locationSlug}`;
    
    // Debug logging for event locations
    useEffect(() => {
        if (props.mediaTags === 'event-locations' || props.isEventLocation) {
            console.log('Carousel mounted with event location props:', {
                isEventLocation,
                locationHref,
                mediaTags: props.mediaTags,
                isEventLocationProp: props.isEventLocation,
                locationSlug: props.locationSlug,
                isActive: props.isActive
            });
        }
    }, []);

    useEffect(() => {
        let currentTags = setCurrentTags();

        if (prevTags.current !== currentTags && !!gallery) {
            loadData();
        }
        prevTags.current = setCurrentTags();

        isMounted = true;
        smallScreen = window.innerWidth <= 767 ? true : false;

        if (!!swiperContainer.current) {
            let images = swiperContainer.current.querySelectorAll('.lazy-image');

            intersectObservers.push(lazyImages(images, function (img) {
                let pictureNode = img.parentNode;
                img.addEventListener('load', e => {
                    loadedThumbnail.current = {};
                    loadedThumbnail.current.src = img.currentSrc;
                    loadedThumbnail.current.width = pictureNode.childNodes[2].width;
                    loadedThumbnail.current.height = pictureNode.childNodes[2].height;
                });
            }));

            swiperContainer.current.addEventListener("mouseenter", initSwiper);
            // swiperContainer.current.addEventListener("touchstart", initSwiper, { passive: true });
        }

        // Load gallery when carousel is in view.
        let observer = new IntersectionObserver((entries, observer) => {
            carouselInView(entries, observer);
        }, { threshold: [0, 1] });

        observer.observe(swiperContainer.current);
        intersectObservers.push(observer);

        // run clean up on unmount in return.
        return () => {
            isMounted = false;
            if (!!swiperContainer.current)
                swiperContainer.current.removeEventListener("mouseenter", initSwiper);

            intersectObservers.forEach(observer => observer.disconnect());
        };
    }, [props.isActive, props.mediaTags, gallery]);

    const setCurrentTags = () => {
        return !!props.mediaTags ? props.mediaTags.toString() : null;
    }

    const getContainerClasses = () => {
        return classnames(
            !!props.className ? props.className : null,
            !props.isActive ? 'd-block il-location-carousel il-carousel has-scrollbar' : null,
            'position-relative order-first',
        )
    }

    function carouselInView(entries, observer) {
        entries.forEach(entry => {
            if (entry.intersectionRatio > 0) {
                if (!gallery) {
                    loadData();
                    observer.unobserve(entry.target);
                }
            }
        });
    }

    const initSwiper = () => {
        if (window.innerWidth >= 768) {
            loadStylesheet();
            if (gallery && !!props.onActiveCallback) {
                props.onActiveCallback(props.locationId);
            }
        }
    };

    const loadData = () => {
        let endpoint = '/api/location/' + props.locationId + "/media";
        loadedThumbnail.current = null;

        let queryParams = [];

        if (!!props.mediaTags) {
            if (Array.isArray(props.mediaTags) && props.mediaTags.length) {
                props.mediaTags.forEach((tag, key, array) => {
                    queryParams.push(`media_tag[]=${encodeURI(tag)}`);
                });
            } else {
                queryParams.push(`media_tag[]=${encodeURI(props.mediaTags)}`);
            }
        }
        
        // Check if this is an event-locations category or if we're on an event location page
        if (props.mediaTags === 'event-locations' || (typeof isEventLocation !== 'undefined' && isEventLocation)) {
            queryParams.push('event_category=true');
        }
        
        if (queryParams.length > 0) {
            endpoint += `?${queryParams.join('&')}`;
        }

        axios.get(endpoint).then(response => {
            if (isMounted) {
                setGallery(response.data);
            }
        }).catch(error => {
            console.log(error);
        });
    }

    const loadImages = (swiper, numberOfImages = 3) => {
        let activeKey = swiper.activeIndex;
        for (let i = 0; i < numberOfImages; i++) {
            let img = swiper.imagesToLoad[activeKey + i];

            if (!!img && !img.classList.contains('loaded')) {
                loadImageSrc(img)
            }
        }
    }

    const getTagSlides = () => {
        let tagSlides = [];

        if (!!gallery && !!gallery['tagMatched']) {
            for (const tag in gallery['tagMatched']) {
                gallery['tagMatched'][tag].forEach((img, key) => {
                    tagSlides.push(
                        <SwiperSlide key={tag + key} className={classnames('swiper-slide', key === 0 ? 'w-100' : "w-auto")}>
                            <Image
                                containerClasses={classnames("il-image-container img-fluid d-block h-100", key === 0 ? 'w-100' : "w-auto")}
                                imageClasses={classnames("h-100 location-thumb", key === 0 ? 'w-100 object-fit' : 'w-auto')}
                                title={tag}
                                img={img}
                                href={!!props.locationSlug ? locationHref : "#"}
                                locationTitle={props.title}
                                locationSlug={props.locationSlug}
                                locationId={props.locationId}
                            />
                            <span className="caption">{tag}</span>
                        </SwiperSlide>
                    );
                });
            }
        };

        return tagSlides
    }

    let CustomContainerTag = !props.isActive && !props.isFeatured ? 'a' : 'div';

    // const CustomContainerTag = 'div';

    return (
        <CustomContainerTag
            href={!props.isActive ? locationHref : null}
            target={!props.isActive ? '_blank' : null}
            ref={swiperContainer}
            className={getContainerClasses()}
        >
            {!props.isActive && !gallery && (
                <div className="il-pre-carousel-thumb"></div>
            )}

            {!!props.isActive && !!gallery && (
                <Swiper
                    modules={[Navigation, Scrollbar, Mousewheel]}
                    className="il-location-carousel il-carousel il-component has-scrollbar d-flex flex-column"
                    spaceBetween={3}
                    slidesPerView="auto"
                    freeMode={{
                        enabled: true,
                    }}
                    navigation
                    mousewheel={{ forceToAxis: true }}
                    scrollbar={{ draggable: true }}
                    resistanceRatio={0}
                    onProgress={(swiper) => {
                        if (smallScreen) {
                            loadImages(swiper, 10);
                        } else {
                            if (typeof props.swiperProgressCallback !== 'undefined') {
                                props.swiperProgressCallback(swiper);
                            }
                            loadImages(swiper);
                        }
                    }}>

                    {props.children}

                    {getTagSlides()}

                    {!!gallery && !gallery['tagMatched'] && !!gallery['location-feature'] && (
                        <SwiperSlide className="swiper-slide w-100">
                            {/* <a target="_blank" href=> */}
                            <Image
                                containerClasses="il-image-container img-fluid d-block w-100 h-100"
                                imageClasses="h-100 w-100 object-fit location-thumb"
                                title={props.title}
                                img={gallery['location-feature'][0]}
                                href={!!props.locationSlug ? locationHref : "#"}
                                locationTitle={props.title}
                                locationSlug={props.locationSlug}
                                locationId={props.locationId}
                            />
                            {/* </a> */}
                        </SwiperSlide>
                    )}

                    {!!gallery && !!gallery['location-gallery'] && gallery['location-gallery'].map((img, key) => (
                        <SwiperSlide key={key} className={classnames('swiper-slide', key === 0 ? 'w-100' : "w-auto h-100")}>
                            {/* <a target="_blank" href={!!props.locationSlug ? locationHref : "#"}> */}
                            <Image
                                containerClasses={classnames("il-image-container img-fluid d-block", key === 0 ? 'w-100 h-100' : "w-auto h-100")}
                                imageClasses={classnames(key === 0 ? 'h-100 w-100 object-fit location-thumb' : 'w-auto h-100')}
                                title={props.title}
                                img={img}
                                selectableImg
                                href={!!props.locationSlug ? locationHref : "#"}
                                locationTitle={props.title}
                                locationSlug={props.locationSlug}
                                locationId={props.locationId}
                            />
                            {/* </a> */}
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}

            {!props.isActive && (
                <Fragment>
                    {props.children}

                    {!!gallery && !!gallery['tagMatched'] && (
                        <CarouselPlaceholder
                            loadedThumbnail={loadedThumbnail.current}
                            slug={props.locationSlug}
                            title={props.title}
                            img={gallery['tagMatched'][Object.keys(gallery['tagMatched'])[0]][0]}
                        >
                            <span className="caption">{Object.keys(gallery['tagMatched'])[0]}</span>
                        </CarouselPlaceholder>
                    )}
                    {!!gallery && !gallery['tagMatched'] && !!gallery['location-feature'] && (
                        <Fragment>
                            <CarouselPlaceholder
                                loadedThumbnail={loadedThumbnail.current}
                                slug={props.locationSlug}
                                title={props.title}
                                img={gallery['location-feature'][0]}
                            />
                        </Fragment>
                    )}
                    {!!gallery && !gallery['tagMatched'] && !gallery['location-feature'] && !!gallery['location-gallery'] && (
                        <Fragment>
                            <CarouselPlaceholder
                                loadedThumbnail={loadedThumbnail.current}
                                slug={props.locationSlug}
                                title={props.title}
                                img={gallery['location-gallery'][0]}
                            />
                        </Fragment>
                    )}

                    {!smallScreen && (
                        <Fragment>
                            <div className="swiper-button-prev text-center swiper-button-disabled"></div>
                            <div className="swiper-button-next text-center"></div>
                            <div className="swiper-scrollbar"></div>
                        </Fragment>
                    )}

                </Fragment>
            )}
        </CustomContainerTag>
    );
}

// don't rerender if no props or no state changed.
Carousel = React.memo(Carousel);

export default Carousel;
